<template>
  <div
    class="select-field"
    :class="selectFieldClasses"
    v-click-outside="closeDropdown"
  >
    <!-- Selected Items Display -->
    <div class="select-field__control" @click="toggleDropdown">
      <div class="select-field__value-container">
        <!-- Multiple Select Display -->
        <template v-if="multiple && selectedItems.length > 0">
          <span
            v-for="(item, index) in selectedItems"
            :key="getTagKey(item, index)"
            class="select-field__tag"
          >
            <slot name="tag" :option="item">
              <span class="select-field__tag-label">{{ item[labelKey] }}</span>
            </slot>
            <span
              class="select-field__tag-remove"
              @click.stop="removeItem(item)"
            >
              ×
            </span>
          </span>
        </template>

        <!-- Single Select Display -->
        <template v-else-if="!multiple && selectedItem">
          <slot name="selected" :option="selectedItem">
            <span class="select-field__single-value">{{
              selectedItem[labelKey]
            }}</span>
          </slot>
        </template>

        <!-- Placeholder -->
        <span v-else class="select-field__placeholder">{{ placeholder }}</span>
      </div>

      <!-- Dropdown Arrow -->
      <div class="select-field__indicators">
        <span
          v-if="clearable && hasValue"
          class="select-field__clear"
          @click.stop="clearSelection"
        >
          ×
        </span>
        <span
          class="select-field__arrow"
          :class="{ 'select-field__arrow--up': isOpen }"
        >
          ▼
        </span>
      </div>
    </div>

    <!-- Dropdown -->
    <transition name="select-field__dropdown">
      <div v-if="isOpen" class="select-field__dropdown">
        <!-- Search Input -->
        <div v-if="searchable" class="select-field__search">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="select-field__search-input"
            :placeholder="searchPlaceholder"
            @click.stop
          />
        </div>

        <!-- Options List -->
        <div class="select-field__options">
          <div
            v-for="(option, index) in filteredOptions"
            :key="getOptionKey(option, index)"
            class="select-field__option"
            :class="{
              'select-field__option--selected': isSelected(option),
              'select-field__option--disabled': option.disabled,
            }"
            @click.stop="selectOption(option)"
          >
            <!-- Option Slot -->
            <slot name="option" :option="option" :selected="isSelected(option)">
              <span class="select-field__option-label">{{
                option[labelKey]
              }}</span>
            </slot>
            <span
              v-if="multiple && isSelected(option)"
              class="select-field__option-check"
            >
              ✓
            </span>
          </div>

          <!-- No Results -->
          <div
            v-if="filteredOptions.length === 0"
            class="select-field__no-results"
          >
            <slot name="no-results">
              {{ noResultsText }}
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SelectField',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },

  props: {
    value: {
      type: [String, Number, Array, Object],
      default: null,
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select...',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    noResultsText: {
      type: String,
      default: 'No results found',
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    valueKey: {
      type: String,
      default: null,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
      searchQuery: '',
    }
  },

  computed: {
    selectFieldClasses() {
      return {
        'select-field--open': this.isOpen,
        'select-field--disabled': this.disabled,
        'select-field--multiple': this.multiple,
        'select-field--searchable': this.searchable,
        'select-field--has-value': this.hasValue,
      }
    },

    hasValue() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0
      }
      return (
        this.value !== null && this.value !== undefined && this.value !== ''
      )
    },

    selectedItem() {
      if (this.multiple || !this.hasValue) return null

      // If value is already an object (returnObject mode or no valueKey)
      if (typeof this.value === 'object' && this.value !== null) {
        return this.value
      }

      // If valueKey is specified, find by that key
      if (this.valueKey) {
        return this.options.find((opt) => opt[this.valueKey] === this.value)
      }

      // Otherwise, try to match the entire object
      return this.value
    },

    selectedItems() {
      if (!this.multiple || !Array.isArray(this.value)) return []

      // If returnObject mode or no valueKey, assume array contains full objects
      if (this.returnObject || !this.valueKey) {
        return this.value
      }

      // If valueKey is specified, find objects by their values
      return this.options.filter((opt) =>
        this.value.includes(opt[this.valueKey])
      )
    },

    filteredOptions() {
      if (!this.searchable || !this.searchQuery) {
        return this.options
      }

      const query = this.searchQuery.toLowerCase()
      return this.options.filter((option) =>
        option[this.labelKey].toLowerCase().includes(query)
      )
    },
  },

  methods: {
    toggleDropdown() {
      if (this.disabled) return

      this.isOpen = !this.isOpen

      if (this.isOpen && this.searchable) {
        this.$nextTick(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus()
        })
      }
    },

    closeDropdown() {
      this.isOpen = false
      this.searchQuery = ''
    },

    selectOption(option) {
      if (option.disabled) return

      if (this.multiple) {
        this.toggleMultipleOption(option)
      } else {
        this.selectSingleOption(option)
      }
    },

    selectSingleOption(option) {
      // If returnObject is true or no valueKey is specified, return the full object
      const newValue =
        this.returnObject || !this.valueKey ? option : option[this.valueKey]
      this.$emit('input', newValue)
      this.$emit('change', newValue)
      this.closeDropdown()
    },

    toggleMultipleOption(option) {
      const currentValue = Array.isArray(this.value) ? [...this.value] : []

      // If returnObject is true or no valueKey is specified, work with full objects
      if (this.returnObject || !this.valueKey) {
        const index = currentValue.findIndex((item) =>
          this.isEqual(item, option)
        )
        let newValue
        if (index > -1) {
          newValue = currentValue.filter((_, i) => i !== index)
        } else {
          newValue = [...currentValue, option]
        }
        this.$emit('input', newValue)
        this.$emit('change', newValue)
      } else {
        // Work with valueKey
        const optionValue = option[this.valueKey]
        const index = currentValue.indexOf(optionValue)
        let newValue
        if (index > -1) {
          newValue = currentValue.filter((_, i) => i !== index)
        } else {
          newValue = [...currentValue, optionValue]
        }
        this.$emit('input', newValue)
        this.$emit('change', newValue)
      }
    },

    removeItem(item) {
      if (this.disabled) return

      const currentValue = Array.isArray(this.value) ? [...this.value] : []
      let newValue

      if (this.returnObject || !this.valueKey) {
        newValue = currentValue.filter((val) => !this.isEqual(val, item))
      } else {
        newValue = currentValue.filter((val) => val !== item[this.valueKey])
      }

      this.$emit('input', newValue)
      this.$emit('change', newValue)
    },

    clearSelection() {
      if (this.disabled) return

      const newValue = this.multiple ? [] : null
      this.$emit('input', newValue)
      this.$emit('change', newValue)
    },

    isSelected(option) {
      if (this.multiple) {
        if (!Array.isArray(this.value)) return false

        if (this.returnObject || !this.valueKey) {
          return this.value.some((val) => this.isEqual(val, option))
        }
        return this.value.includes(option[this.valueKey])
      }

      if (
        (this.returnObject || !this.valueKey) &&
        typeof this.value === 'object'
      ) {
        return this.isEqual(this.value, option)
      }

      if (this.valueKey) {
        return this.value === option[this.valueKey]
      }

      return false
    },

    isEqual(obj1, obj2) {
      // Simple equality check for objects
      // You can make this more sophisticated if needed
      if (obj1 === obj2) return true
      if (!obj1 || !obj2) return false

      // If valueKey exists, compare by that key
      if (this.valueKey) {
        return obj1[this.valueKey] === obj2[this.valueKey]
      }

      // Otherwise, check all keys
      const keys1 = Object.keys(obj1)
      const keys2 = Object.keys(obj2)

      if (keys1.length !== keys2.length) return false

      return keys1.every((key) => obj1[key] === obj2[key])
    },

    getOptionKey(option, index) {
      // Generate a unique key for each option
      if (this.valueKey && option[this.valueKey] != null) {
        return `option-${option[this.valueKey]}`
      }

      // If the option has an id or _id property, use it
      if (option.id != null) return `option-id-${option.id}`
      if (option._id != null) return `option-_id-${option._id}`

      // Otherwise use index combined with some property to make it more stable
      const stableProperty = option[this.labelKey] || JSON.stringify(option)
      return `option-${index}-${stableProperty}`
    },

    getTagKey(item, index) {
      // Generate a unique key for each tag
      if (this.valueKey && item[this.valueKey] != null) {
        return `tag-${item[this.valueKey]}`
      }

      // If the item has an id or _id property, use it
      if (item.id != null) return `tag-id-${item.id}`
      if (item._id != null) return `tag-_id-${item._id}`

      // Otherwise use index
      return `tag-${index}`
    },
  },
}
</script>

<style lang="scss" scoped>
// Variables
$select-field-border-color: #ddd !default;
$select-field-border-radius: 4px !default;
$select-field-bg: #fff !default;
$select-field-hover-bg: #f5f5f5 !default;
$select-field-selected-bg: #e3f2fd !default;
$select-field-disabled-bg: #f5f5f5 !default;
$select-field-text-color: #333 !default;
$select-field-placeholder-color: #999 !default;
$select-field-tag-bg: #e0e0e0 !default;
$select-field-tag-color: #333 !default;
$select-field-focus-color: #2196f3 !default;
$select-field-transition: all 0.2s ease !default;

.select-field {
  position: relative;
  width: 100%;

  &__control {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 8px 12px;
    background-color: $select-field-bg;
    border: 1px solid $select-field-border-color;
    border-radius: $select-field-border-radius;
    cursor: pointer;
    transition: $select-field-transition;

    &:hover {
      border-color: darken($select-field-border-color, 10%);
    }
  }

  &--open &__control {
    border-color: $select-field-focus-color;
    box-shadow: 0 0 0 2px rgba($select-field-focus-color, 0.2);
  }

  &--disabled &__control {
    background-color: $select-field-disabled-bg;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &__value-container {
    flex: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-height: 24px;
  }

  &__placeholder {
    color: $select-field-placeholder-color;
    user-select: none;
  }

  &__single-value {
    color: $select-field-text-color;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    background-color: $select-field-tag-bg;
    border-radius: 3px;
    font-size: 14px;

    &-label {
      color: $select-field-tag-color;
      margin-right: 4px;
    }

    &-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 18px;
      line-height: 1;
      transition: $select-field-transition;

      &:hover {
        background-color: darken($select-field-tag-bg, 10%);
      }
    }
  }

  &__indicators {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    color: $select-field-placeholder-color;
    transition: $select-field-transition;

    &:hover {
      background-color: $select-field-hover-bg;
      color: $select-field-text-color;
    }
  }

  &__arrow {
    font-size: 12px;
    color: $select-field-placeholder-color;
    transition: transform 0.2s ease;

    &--up {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background-color: $select-field-bg;
    border: 1px solid $select-field-border-color;
    border-radius: $select-field-border-radius;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 300px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &-enter-active,
    &-leave-active {
      transition: opacity 0.2s ease, transform 0.2s ease;
    }

    &-enter,
    &-leave-to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }

  &__search {
    padding: 8px;
    border-bottom: 1px solid $select-field-border-color;

    &-input {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid $select-field-border-color;
      border-radius: 3px;
      font-size: 14px;
      outline: none;
      transition: $select-field-transition;

      &:focus {
        border-color: $select-field-focus-color;
      }
    }
  }

  &__options {
    flex: 1;
    overflow-y: auto;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    cursor: pointer;
    transition: $select-field-transition;

    &:hover {
      background-color: $select-field-hover-bg;
    }

    &--selected {
      background-color: $select-field-selected-bg;
      font-weight: 500;
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background-color: transparent;
      }
    }

    &-label {
      flex: 1;
      color: $select-field-text-color;
    }

    &-check {
      color: $select-field-focus-color;
      font-weight: bold;
      margin-left: 8px;
    }
  }

  &__no-results {
    padding: 20px;
    text-align: center;
    color: $select-field-placeholder-color;
  }
}
</style>
